import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { getSupabase } from '../lib/supabase';
import { getForgeIDGenerateUrl, getSBTMetadata, verifySBTOwnership, getPolygonScanUrl, formatSBTData } from '../lib/forgeid';
import { FiUpload, FiImage, FiShield, FiExternalLink, FiLoader, FiCheck, FiX } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import toast from 'react-hot-toast';

const AvatarUpload = ({ onAvatarChange }) => {
  const { walletAddress, isAuthenticated } = useAuth();
  const { profile, saveProfile } = useProfile();
  const [uploadMethod, setUploadMethod] = useState('upload'); // 'upload', 'forgeid', 'sbt'
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sbtData, setSbtData] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (profile?.avatar) {
      setPreview(profile.avatar);
    }
    if (profile?.avatar_type === 'sbt' && profile?.sbt_contract_address) {
      setSbtData({
        contractAddress: profile.sbt_contract_address,
        tokenId: profile.sbt_token_id,
        tokenUri: profile.sbt_token_uri,
        network: profile.sbt_network || 'polygon-amoy',
      });
      setUploadMethod('sbt');
    }
  }, [profile]);

  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploading(true);

    try {
      const supabase = getSupabase();
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${walletAddress || 'anonymous'}/${fileName}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      setPreview(publicUrl);

      // Update profile
      const result = await saveProfile({
        avatar: publicUrl,
        avatar_type: 'upload',
        sbt_contract_address: null,
        sbt_token_id: null,
        sbt_token_uri: null,
        sbt_network: null,
      });

      if (result.success) {
        toast.success('Avatar uploaded successfully!');
        if (onAvatarChange) onAvatarChange(publicUrl);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Handle ForgeID generation
  const handleForgeIDGenerate = () => {
    if (!isAuthenticated) {
      toast.error('Please connect your wallet first');
      return;
    }

    const returnUrl = `${window.location.origin}/#/profile?forgeid=callback`;
    const forgeIDUrl = getForgeIDGenerateUrl(returnUrl);
    window.open(forgeIDUrl, '_blank', 'width=1200,height=800');
  };

  // Handle ForgeID callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('forgeid') === 'callback') {
      handleForgeIDCallback(urlParams);
    }
  }, []);

  const handleForgeIDCallback = async (urlParams) => {
    setVerifying(true);
    try {
      const parsedData = parseForgeIDCallback(urlParams);
      const sbtData = formatSBTData(parsedData);
      
      if (!sbtData.contractAddress || !sbtData.tokenId) {
        toast.error('Invalid SBT data received');
        return;
      }

      // Verify ownership
      const isOwner = await verifySBTOwnership(
        sbtData.contractAddress,
        sbtData.tokenId,
        walletAddress,
        sbtData.network
      );

      if (!isOwner) {
        toast.error('You do not own this SBT');
        return;
      }

      // Get metadata
      const { data: metadata } = await getSBTMetadata(sbtData.tokenUri);
      const imageUrl = metadata?.image || sbtData.imageUrl;

      setSbtData(sbtData);
      setPreview(imageUrl);

      // Update profile
      const result = await saveProfile({
        avatar: imageUrl,
        avatar_type: 'sbt',
        sbt_contract_address: sbtData.contractAddress,
        sbt_token_id: sbtData.tokenId,
        sbt_token_uri: sbtData.tokenUri,
        sbt_network: sbtData.network,
      });

      if (result.success) {
        toast.success('SBT avatar set successfully!');
        if (onAvatarChange) onAvatarChange(imageUrl);
        
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error handling ForgeID callback:', error);
      toast.error(`Failed to set SBT avatar: ${error.message}`);
    } finally {
      setVerifying(false);
    }
  };

  // Handle SBT input (manual entry)
  const handleSBTInput = async () => {
    const contractAddress = prompt('Enter SBT contract address:');
    const tokenId = prompt('Enter token ID:');
    const network = prompt('Enter network (polygon-amoy/polygon):', 'polygon-amoy');

    if (!contractAddress || !tokenId) {
      return;
    }

    setVerifying(true);
    try {
      // Verify ownership
      const isOwner = await verifySBTOwnership(contractAddress, tokenId, walletAddress, network);
      
      if (!isOwner) {
        toast.error('You do not own this SBT');
        return;
      }

      // Get token URI (would need contract call)
      // For now, use placeholder
      const sbtData = {
        contractAddress,
        tokenId,
        tokenUri: `ipfs://...`, // Would fetch from contract
        network,
      };

      setSbtData(sbtData);
      
      // Update profile
      const result = await saveProfile({
        avatar_type: 'sbt',
        sbt_contract_address: contractAddress,
        sbt_token_id: tokenId,
        sbt_token_uri: sbtData.tokenUri,
        sbt_network: network,
      });

      if (result.success) {
        toast.success('SBT avatar set successfully!');
      }
    } catch (error) {
      console.error('Error setting SBT:', error);
      toast.error(`Failed to set SBT: ${error.message}`);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Avatar Preview */}
      <div className="flex items-center space-x-6">
        <div className="relative">
          {preview ? (
            <img
              src={preview}
              alt="Avatar preview"
              className="w-24 h-24 rounded-full object-cover border-2 border-lime-500/50 shadow-glow-lime-sm"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-dark-800 border-2 border-dark-700 flex items-center justify-center">
              <SafeIcon icon={FiImage} className="text-4xl text-dark-500" />
            </div>
          )}
          {profile?.avatar_type === 'sbt' && sbtData && (
            <div className="absolute -top-1 -right-1 bg-lime-500 rounded-full p-1">
              <SafeIcon icon={FiShield} className="text-xs text-dark-950" />
            </div>
          )}
        </div>

        <div className="flex-1">
          <h3 className="text-sm font-medium text-dark-200 mb-1">Profile Avatar</h3>
          <p className="text-xs text-dark-400">
            {profile?.avatar_type === 'sbt' ? 'Soulbound Token Avatar' : 'Uploaded Avatar'}
          </p>
          {sbtData && (
            <a
              href={getPolygonScanUrl(sbtData.contractAddress, sbtData.tokenId, sbtData.network)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-lime-400 hover:text-lime-300 inline-flex items-center mt-1"
            >
              View on PolygonScan
              <SafeIcon icon={FiExternalLink} className="ml-1 text-xs" />
            </a>
          )}
        </div>
      </div>

      {/* Upload Method Tabs */}
      <div className="flex space-x-2 border-b border-dark-700">
        <button
          onClick={() => setUploadMethod('upload')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            uploadMethod === 'upload'
              ? 'border-lime-500 text-lime-400'
              : 'border-transparent text-dark-400 hover:text-dark-200'
          }`}
        >
          Upload
        </button>
        <button
          onClick={() => setUploadMethod('forgeid')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            uploadMethod === 'forgeid'
              ? 'border-lime-500 text-lime-400'
              : 'border-transparent text-dark-400 hover:text-dark-200'
          }`}
        >
          ForgeID
        </button>
        <button
          onClick={() => setUploadMethod('sbt')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            uploadMethod === 'sbt'
              ? 'border-lime-500 text-lime-400'
              : 'border-transparent text-dark-400 hover:text-dark-200'
          }`}
        >
          Use SBT
        </button>
      </div>

      {/* Upload Method Content */}
      <div className="mt-4">
        {uploadMethod === 'upload' && (
          <div className="space-y-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full glass-lime text-lime-400 px-4 py-3 rounded-lg font-medium hover:text-lime-300 transition-colors border border-lime-500/50 hover:border-lime-400/70 shadow-glow-lime-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
            >
              <SafeIcon icon={uploading ? FiLoader : FiUpload} className={`mr-2 ${uploading ? 'animate-spin' : ''}`} />
              {uploading ? 'Uploading...' : 'Choose Image'}
            </button>
            <p className="text-xs text-dark-400 text-center">
              PNG, JPG, or GIF up to 5MB
            </p>
          </div>
        )}

        {uploadMethod === 'forgeid' && (
          <div className="space-y-4">
            <div className="glass-dark p-4 rounded-lg border border-dark-700">
              <h4 className="text-sm font-medium text-lime-bright mb-2">Generate with ForgeID</h4>
              <p className="text-xs text-dark-300 mb-4">
                Create an AI-powered Soulbound Avatar using ForgeID. Your avatar will be minted as an SBT on Polygon.
              </p>
              <button
                onClick={handleForgeIDGenerate}
                disabled={!isAuthenticated || verifying}
                className="w-full glass-lime text-lime-400 px-4 py-3 rounded-lg font-medium hover:text-lime-300 transition-colors border border-lime-500/50 hover:border-lime-400/70 shadow-glow-lime-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
              >
                <SafeIcon icon={verifying ? FiLoader : FiShield} className={`mr-2 ${verifying ? 'animate-spin' : ''}`} />
                {verifying ? 'Verifying...' : 'Generate Avatar with ForgeID'}
              </button>
              {!isAuthenticated && (
                <p className="text-xs text-dark-400 text-center mt-2">
                  Connect your wallet to generate SBT avatars
                </p>
              )}
            </div>
          </div>
        )}

        {uploadMethod === 'sbt' && (
          <div className="space-y-4">
            <div className="glass-dark p-4 rounded-lg border border-dark-700">
              <h4 className="text-sm font-medium text-lime-bright mb-2">Use Existing SBT</h4>
              <p className="text-xs text-dark-300 mb-4">
                Enter your SBT contract address and token ID to use an existing Soulbound Avatar.
              </p>
              <button
                onClick={handleSBTInput}
                disabled={!isAuthenticated || verifying}
                className="w-full glass text-white px-4 py-3 rounded-lg font-medium hover:text-lime-400 transition-colors border border-dark-700 hover:border-lime-500/50 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
              >
                <SafeIcon icon={verifying ? FiLoader : FiShield} className={`mr-2 ${verifying ? 'animate-spin' : ''}`} />
                {verifying ? 'Verifying...' : 'Enter SBT Details'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AvatarUpload;

